import React from 'react'
import { connect } from 'react-redux'
import TetherComponent from 'react-tether'

import { Config } from '@leverege/plugin'
import { Attributes } from '@leverege/ui-attributes'
import { Dialogs, Flex, Pane, PropertyGrid, Text } from '@leverege/ui-elements'
import { ContextMenu } from '@leverege/ui-plugin'
import { Selection } from '@leverege/ui-redux'
import { DateTime, Duration } from 'luxon'

import { buildImageUrl, formatDistance } from '../../../util/Format'
import S from './LinearViewBlock.css'
import CopyEventEndImageUrl from './actions/CopyEventEndImageUrl'
import CopyEventEndVideoUrl from './actions/CopyEventEndVideoUrl'
import CopyEventStartImageUrl from './actions/CopyEventStartImageUrl'
import CopyEventStartVideoUrl from './actions/CopyEventStartVideoUrl'
import OpenEventEndImageUrl from './actions/OpenEventEndImageUrl'
import OpenEventEndVideoUrl from './actions/OpenEventEndVideoUrl'
import OpenEventStartImageUrl from './actions/OpenEventStartImageUrl'
import OpenEventStartVideoUrl from './actions/OpenEventStartVideoUrl'

const EVENT_LABELS = {
  dogInStation : 'Dog in Station',
}

const matchContext = {
  client : 'LinearViewBlock'
}

class LinearViewBlock extends React.Component {
  get actionContext() {
    const { item } = this.props
    return {
      item
    }
  }

  onContextMenu = ( evt ) => {
    const { isAdmin } = this.props
    
    if ( isAdmin ) {
      showPopup( evt, matchContext, this.actionContext )
    }
  }
  
  renderPopup = ( ref ) => {
    const { isAdmin } = this.props

    let content
    
    const { rollover, item } = this.props

    const isRollover = Selection.isSelected( rollover, item.id )

    if ( isRollover ) {
      const { startData, stopData, leadTime, leadTimeAverage } = item

      const gcsBucketUrl = Config.get( 'LinearViewBlock', 'gcsImageBucketUrl' )
      const startImagePath = startData?.imageName || startData?.imagePath
      const stopImagePath = stopData?.imageName || stopData?.imagePath
      const startImageSrc = startImagePath && buildImageUrl( gcsBucketUrl, startImagePath )
      const stopImageSrc = stopImagePath && buildImageUrl( gcsBucketUrl, stopImagePath )

      const duration = formatDistance( item.startTime, item.startTime === item.stopTime ? Date.now() : item.stopTime )
      const timeBetweenServices = leadTime ?
        Duration
          .fromMillis( leadTime )
          .rescale()
          .set( { milliseconds : 0 } )
          .rescale()
          .toHuman( { unitDisplay : 'short', listStyle : 'narrow' } ) :
        null
      const averageTimeBetweenServices = leadTimeAverage ?
        Duration
          .fromMillis( leadTimeAverage )
          .rescale()
          .set( { milliseconds : 0 } )
          .rescale()
          .toHuman( { unitDisplay : 'short', listStyle : 'narrow' } ) :
        null

      const timeBetweenRenderer = [
        <PropertyGrid.Subheader key="header" title="Time Between Services" />,
        <PropertyGrid.Item key="value" label="Time Between Last Service">
          <Text variant="detailValue">{timeBetweenServices}</Text>
        </PropertyGrid.Item>,
        <PropertyGrid.Item key="average" label="Running Average">
          <Text variant="detailValue">{averageTimeBetweenServices}</Text>
        </PropertyGrid.Item>
      ]

      if ( !isAdmin ) {
        content = (
          <PropertyGrid variant="stationDetailCard|detailCard" className={S.propertyGrid}>
            <PropertyGrid.Header title={EVENT_LABELS[item.type]} />
            <PropertyGrid.Subheader title="Current Service" />
            <PropertyGrid.Item label="Start Time">
              <Text variant="detailValue">{DateTime.fromMillis( item.startTime ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}</Text>
            </PropertyGrid.Item>
            {stopImagePath && (
              <PropertyGrid.Item label="End Time">
                <Text variant="detailValue">{DateTime.fromMillis( item.stopTime ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}</Text>
              </PropertyGrid.Item>
            )}
            <PropertyGrid.Item label="Duration">
              <Text variant="detailValue">{duration}</Text>
            </PropertyGrid.Item>
            {timeBetweenServices && timeBetweenRenderer}
          </PropertyGrid>
        )
      } else {
        content = (
          <PropertyGrid variant="stationDetailCard|detailCard" className={S.propertyGrid}>
            <PropertyGrid.Header title={EVENT_LABELS[item.type]} />
            <PropertyGrid.Item fillCell>
              <Flex direction="row" justify="flex-start">
                {startImageSrc && <StationImage src={startImageSrc} />}
                {stopImageSrc && <StationImage src={stopImageSrc} />}
              </Flex>
            </PropertyGrid.Item>
            <PropertyGrid.Subheader title="Current Service" />
            <PropertyGrid.Item label="Start Time">
              <Text variant="detailValue">{DateTime.fromMillis( item.startTime ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}</Text>
            </PropertyGrid.Item>
            {stopImagePath && (
              <PropertyGrid.Item label="End Time">
                <Text variant="detailValue">{DateTime.fromMillis( item.stopTime ).toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS )}</Text>
              </PropertyGrid.Item>
            )}
            <PropertyGrid.Item label="Duration">
              <Text variant="detailValue">{duration}</Text>
            </PropertyGrid.Item>
            {timeBetweenServices && timeBetweenRenderer}
          </PropertyGrid>
        )
      }

      return (
        <Pane
          variant="linearViewBlockTooltip|dialog"
          domRef={ref}
          style={{ position : 'relative', top : 0, left : 0 }}>
          {content}
        </Pane>
      )
    }

    return null
  }

  renderTarget = ( ref ) => {
    const { item, x1, x2, rollover, dispatch, variant, isAdmin, ...rest } = this.props

    const isLift = item.type === 'carOnLift'

    const { obj } = item

    const number = Attributes.get( 'station.number', obj )

    return (
      <Pane
        onContextMenu={this.onContextMenu}
        domRef={ref}
        {...rest}
        variant={isLift ?
          `${variant}LiftStation${number}|${variant}Lift|${variant}Station${number}|${variant}` :
          `${variant}Station${number}|${variant}`} />
    )
  }

  render() {
    const { item } = this.props

    if ( item.type === 'carOnJack' ) {
      return null
    }

    return (
      <TetherComponent
        attachment="bottom left"
        targetAttachment="top left"
        constraints={[ {
          to : 'window',
          pin : true
        } ]}
        renderTarget={this.renderTarget}
        renderElement={this.renderPopup} />
    )
  }
}

function showPopup(
  event,
  matchContext,
  context,
  actions = [
    CopyEventStartImageUrl,
    CopyEventEndImageUrl,
    CopyEventStartVideoUrl,
    CopyEventEndVideoUrl,
    OpenEventStartImageUrl,
    OpenEventEndImageUrl,
    OpenEventStartVideoUrl,
    OpenEventEndVideoUrl
  ]
) {
  event.preventDefault()
  const position = { left : `${event.clientX}px`, top : `${event.clientY}px` }

  Dialogs.show( {
    component : ContextMenu,
    props : { position, actions, matchContext, context }
  } )
}

export default connect( ( state ) => {
  const roles = state.ui?.personas

  return {
    rollover : Selection.get( state, 'rollover' ),
    selected : Selection.get( state, 'selected' ),
    isAdmin : roles.some( role => role.name === 'default' )
  }
} )( LinearViewBlock )

function StationImage( { src } ) {
  return (
    <div
      className={S.image}
      style={{ backgroundImage : `url("${encodeURI( src )}")` }} />
  )
}
